import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Bell, Activity, Loader2, MessageSquare } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useNotificationItems, type NotificationItem } from '@/hooks/useNotificationItems'
import { useTeamContext } from '@/hooks/useTeamContext'
import { USER_ROLES } from '@/config/permissions'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import {
  getMissionStatusBadgeProps,
  getMissionStatusLabel,
  isFreelancerMissionActionDisplayStatus,
} from '@/constants/missionStatus'
import { Badge } from '@/components/ui/badge'
import { isMissionNotificationEligible } from '@/utils/missionCompanyNotification'
import { useDeferredReady } from '@/hooks/useDeferredReady'
import { useNavCompact } from '@/hooks/use-nav-compact'
import { useSidebar } from '@/components/ui/sidebar'

function NotificationItemRow({
  item,
  onNavigate,
}: {
  item: NotificationItem
  onNavigate: (path: string) => void
}) {
  const { t } = useTranslation()
  const { effectiveRole } = useTeamContext()
  const isFreelancerUser = effectiveRole === USER_ROLES.FREELANCER
  const isMission = item.type === 'mission'
  const isComment = item.type === 'comment'
  const missionIconClass = isMission
    ? getMissionStatusBadgeProps(item.displayStatus).className
    : isComment
      ? 'bg-violet-100 text-violet-700'
      : 'bg-blue-100 text-blue-700'
  const showStatusBell =
    isMission &&
    !!item.displayStatus &&
    isMissionNotificationEligible(item.displayStatus) &&
    (isFreelancerUser
      ? isFreelancerMissionActionDisplayStatus(item.displayStatus)
      : item.displayStatus === 'pending-company-review' ||
        item.displayStatus === 'pending-approval')

  return (
    <button
      type="button"
      onClick={() => onNavigate(item.path)}
      className="flex items-start gap-3 w-full px-3 py-2.5 text-left hover:bg-muted/50 rounded-md transition-colors cursor-pointer"
    >
      <div
        className={cn(
          'mt-0.5 flex items-center justify-center w-7 h-7 rounded-full shrink-0',
          missionIconClass
        )}
      >
        {isMission ? <Bell size={14} /> : isComment ? <MessageSquare size={14} /> : <Activity size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {item.title || '--'}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {isComment
            ? item.commentContext === 'activity_report' && item.month
              ? `${item.month} · ${item.description}`
              : item.description
            : isMission
            ? item.freelancer
              ? `${t('notificationCenter.freelancer')}: ${item.freelancer}`
              : t('notificationCenter.missionPending')
            : item.freelancer
              ? `${item.freelancer}${item.month ? ` - ${item.month}` : ''}`
              : item.month
                ? item.month
                : t('notificationCenter.craPending')}
        </p>
        <p className="text-[11px] text-muted-foreground/70 mt-0.5">
          {isComment
            ? t('notificationCenter.actionComment')
            : isMission
            ? isFreelancerUser
              ? t('notificationCenter.actionMission')
              : item.companySignatureContext === 'mission_approval'
                ? t('notificationCenter.actionMissionApproval')
                : item.companySignatureContext === 'addendum'
                  ? t('notificationCenter.actionCompanyAddendum')
                  : t('notificationCenter.actionCompanyDocument')
            : t('notificationCenter.actionCra')}
        </p>
        {isMission && item.displayStatus && (
          <Badge
            variant="outline"
            className={cn('mt-1.5 text-[10px] px-1.5 py-0', missionIconClass)}
          >
            <span className="flex items-center gap-1">
              {showStatusBell && <Bell className="h-3 w-3 shrink-0" />}
              {getMissionStatusLabel(t, item.displayStatus)}
            </span>
          </Badge>
        )}
      </div>
    </button>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <Bell size={24} className="mb-2 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  )
}

function NotificationList({
  items,
  onNavigate,
  emptyMessage,
  isLoading = false,
}: {
  items: NotificationItem[]
  onNavigate: (path: string) => void
  emptyMessage: string
  isLoading?: boolean
}) {
  if (isLoading && items.length === 0) {
    return (
      <div className="min-h-[260px] flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin opacity-60" aria-hidden />
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <div className="min-h-[260px] flex items-center justify-center">
        <EmptyState message={emptyMessage} />
      </div>
    )
  }

  return (
    <div className="min-h-[260px] max-h-[325px] overflow-y-auto -mx-1 px-1">
      {items.map((item, index) => (
        <React.Fragment key={`${item.type}-${item.id}`}>
          <NotificationItemRow item={item} onNavigate={onNavigate} />
          {index < items.length - 1 && <Separator />}
        </React.Fragment>
      ))}
    </div>
  )
}

export function NotificationCenter() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const isNavCompact = useNavCompact()
  const { setOpenMobile } = useSidebar()

  const notificationsIdleReady = useDeferredReady()

  const {
    items,
    missionItems,
    activityReportItems,
    totalCount,
    isLoading,
  } = useNotificationItems({ enabled: notificationsIdleReady })

  const handleNavigate = (path: string) => {
    setOpen(false)
    if (isNavCompact) {
      setOpenMobile(false)
    }
    navigate(path)
  }

  const tabTriggerClass =
    'relative flex items-center cursor-pointer gap-2 px-4 pt-0 pb-3 font-medium text-sm rounded-none border-b-2 border-transparent data-[state=active]:text-foreground data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=inactive]:text-gray-500 hover:text-foreground transition-colors'

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            className="relative flex items-center justify-center w-8 h-8 rounded-md border border-white/20 hover:border-white/30 hover:bg-white/15 transition-colors cursor-pointer"
            aria-label={t('notificationCenter.title')}
          >
            <Bell size={18} className="text-white" />
            {totalCount > 0 && (
              <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-red-500 pointer-events-none" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side={isNavCompact ? 'bottom' : 'right'}
          align="start"
          sideOffset={8}
          collisionPadding={16}
          onOpenAutoFocus={(e: Event) => e.preventDefault()}
          onCloseAutoFocus={(e: Event) => e.preventDefault()}
          className={cn(
            'w-[380px] max-w-[calc(100vw-2rem)] p-0 rounded-xl shadow-xl border',
            isNavCompact && 'mobile-nav-dropdown'
          )}
        >
          <div className="px-4 pt-4 pb-2">
            <h3 className="text-base font-semibold">
              {t('notificationCenter.title')}
            </h3>
          </div>

          <Tabs defaultValue="all" className="w-full mt-2">
            <div className="border-b border-gray-200">
              <TabsList className="w-auto bg-transparent p-0 h-auto gap-0 px-4 justify-start">
                <TabsTrigger value="all" className={tabTriggerClass}>
                  {t('notificationCenter.tabs.all')}
                </TabsTrigger>
                <TabsTrigger value="missions" className={tabTriggerClass}>
                  {t('notificationCenter.tabs.missions')}
                </TabsTrigger>
                <TabsTrigger value="cra" className={tabTriggerClass}>
                  {t('notificationCenter.tabs.cra')}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="px-2">
              <TabsContent value="all">
                <NotificationList
                  items={items}
                  onNavigate={handleNavigate}
                  emptyMessage={t('notificationCenter.empty.all')}
                  isLoading={isLoading}
                />
              </TabsContent>
              <TabsContent value="missions">
                <NotificationList
                  items={missionItems}
                  onNavigate={handleNavigate}
                  emptyMessage={t('notificationCenter.empty.missions')}
                  isLoading={isLoading}
                />
              </TabsContent>
              <TabsContent value="cra">
                <NotificationList
                  items={activityReportItems}
                  onNavigate={handleNavigate}
                  emptyMessage={t('notificationCenter.empty.cra')}
                  isLoading={isLoading}
                />
              </TabsContent>
            </div>
          </Tabs>
        </PopoverContent>
      </Popover>
    </>
  )
}
